import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from '../../auth/api/api.utils';

import { CreateKundeDTO, Kunde, Interessent, KontaktTyp, UpdateKundeDTO } from './types';

export const kundeApi = createApi({
  reducerPath: 'kundeApi',
  tagTypes: ['Kunde', 'Interessent'],
  baseQuery: baseQueryWithReauth,
  endpoints: builder => ({
    createKunde: builder.mutation<Kunde, CreateKundeDTO>({
      query(body) {
        return {
          url: 'kunde',
          method: 'POST',
          body,
        };
      },
      invalidatesTags: result =>
        result
          ? [
              { type: 'Kunde', id: 'LIST' },
              { type: 'Interessent', id: 'LIST' },
            ]
          : [],
    }),
    updateKunde: builder.mutation<Kunde, UpdateKundeDTO>({
      query({ id, ...dto }) {
        return {
          url: `kunde/${id}`,
          method: 'PATCH',
          body: dto,
        };
      },
      invalidatesTags: result =>
        result
          ? [
              { type: 'Kunde', id: 'LIST' },
              { type: 'Kunde', id: result.id },
              { type: 'Interessent', id: 'LIST' },
              { type: 'Interessent', id: result.id },
            ]
          : [],
    }),
    findKundeById: builder.query<Kunde, string>({
      query: id => `kunde/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Kunde', id }],
    }),
    findAllKunde: builder.query<Kunde[], void>({
      query: () => ({
        url: 'kunde',
        params: { typ: KontaktTyp.KUNDE },
      }),
      providesTags: result =>
        result
          ? [
              ...result.map(
                ({ id }) =>
                  ({
                    type: 'Kunde',
                    id,
                  }) as const
              ),
              { type: 'Kunde', id: 'LIST' },
            ]
          : [{ type: 'Kunde', id: 'LIST' }],
    }),
    findAllInteressent: builder.query<Interessent[], void>({
      query: () => ({
        url: 'kunde',
        params: { typ: KontaktTyp.INTERESSENT },
      }),
      providesTags: result =>
        result
          ? [
              ...result.map(
                ({ id }) =>
                  ({
                    type: 'Interessent',
                    id,
                  }) as const
              ),
              { type: 'Interessent', id: 'LIST' },
            ]
          : [{ type: 'Interessent', id: 'LIST' }],
    }),
    convertInteressentToKunde: builder.mutation<Kunde, string>({
      query: id => ({
        url: `kunde/${id}/convert-to-kunde`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Interessent', id },
        { type: 'Interessent', id: 'LIST' },
        { type: 'Kunde', id },
        { type: 'Kunde', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useCreateKundeMutation,
  useFindKundeByIdQuery,
  useFindAllKundeQuery,
  useFindAllInteressentQuery,
  useUpdateKundeMutation,
  useConvertInteressentToKundeMutation,
} = kundeApi;
